import { z } from 'zod';
/**
 * Контракт сводного дашборда поддержки (`GET /api/support-dashboard`).
 *
 * ЕДИНИЦА СЧЁТА — ЧЕЛОВЕК, А НЕ ОБРАЩЕНИЕ. Один клиент с пятью диалогами —
 * единица во всех метриках. Ключ тождества человека — `contact.wit_user_id`
 * (идентификатор в биллинге WIT), потому что один и тот же человек пишет с
 * разных адресов и в разные каналы, и склеивать его по `contact.id` значило бы
 * считать одного клиента трижды.
 *
 * ОТСЮДА ЖЕ ГЛАВНОЕ ОГРАНИЧЕНИЕ: у контакта, которого биллинг ещё не опознал,
 * `wit_user_id` пуст, а значит про него НЕЛЬЗЯ узнать ни наличие алертов
 * (`risk_case` привязан к `wit_user_id`), ни статус кита. Такие обращения не
 * выбрасываются молча — они вынесены в отдельное число
 * {@link SupportDashboardSchema | `contacted.unidentified`}, чтобы было видно,
 * какая доля базы вообще недоступна для этих метрик. Знаменателем метрик про
 * алертов служит `contacted.identified`, а НЕ `contacted.total`: делить на
 * общее число значило бы записывать неопознанных в «без алертов», то есть
 * занижать каждую долю тем сильнее, чем хуже работает идентификация.
 *
 * ПРОЦЕНТЫ ЗДЕСЬ НЕ СЧИТАЮТСЯ. Каждая метрика отдаётся парой «числитель +
 * знаменатель» сырыми числами; «12 из 47» клиент собирает сам. Готовая доля в
 * ответе лишила бы его знаменателя, а без знаменателя «26 %» неотличимы от
 * «26 % по трём наблюдениям».
 */
/**
 * Когорта клиентов по причине обращения плюс срез по алертам и китам внутри
 * неё. Четыре числа, а не два, потому что доли считаются по РАЗНЫМ
 * знаменателям: `withAlerts` — из `users`, `whalesWithAlerts` — из `whales`.
 *
 * Инварианты, которые обязаны выполняться в любом ответе:
 * `withAlerts <= users`, `whales <= users`, `whalesWithAlerts <= whales`,
 * `whalesWithAlerts <= withAlerts`.
 */
export const DashboardCohortSchema = z.object({
    /** Знаменатель когорты: сколько РАЗНЫХ опознанных людей в неё попало. */
    users: z.number().int().nonnegative(),
    /** Из них: у скольких есть хотя бы один кейс платёжного риска. */
    withAlerts: z.number().int().nonnegative(),
    /** Из `users`: сколько китов (порог — `whaleMinCharges` ответа). */
    whales: z.number().int().nonnegative(),
    /** Из `whales`: у скольких есть хотя бы один кейс платёжного риска. */
    whalesWithAlerts: z.number().int().nonnegative(),
});
export const SupportDashboardSchema = z.object({
    /**
     * Границы периода ровно в том виде, в каком они применились. `null` —
     * граница не задана (по умолчанию период открыт с обеих сторон, то есть «за
     * всё время»). Возвращаются, чтобы подпись под цифрами бралась из ответа, а
     * не из того, что клиент ДУМАЕТ, что отправил.
     */
    period: z.object({
        from: z.string().datetime().nullable(),
        to: z.string().datetime().nullable(),
    }),
    /**
     * Порог «кита» в успешных списаниях на момент запроса (настройка
     * `billing.whale_min_charges`). Отдаётся вместе с цифрами, потому что число
     * китов без порога нечитаемо: «40 китов» при пороге 2 и при пороге 20 —
     * разные утверждения, а порог оператор меняет в панели.
     */
    whaleMinCharges: z.number().int().positive(),
    /**
     * Метрика 1: сколько людей обратилось в поддержку за период.
     *
     * ЭТО НЕ ВСЕ ДИАЛОГИ ПЕРИОДА. Из популяции целиком — и из числителей, и из
     * знаменателей — вычтены два класса диалогов, которые обращением клиента не
     * являются: попавшие в чёрный список (подавленный спам, панель их не
     * показывает) и распознанные как письма организаций
     * (`conversation.sender_capacity = 'business'` — вендоры, счета, рассылки;
     * значение эффективное, то есть с учётом ручного отзыва статуса оператором).
     * Неклассифицированные (`sender_capacity IS NULL`) организациями НЕ
     * считаются и остаются в расчёте.
     *
     * Исключение живёт на ДИАЛОГЕ, а не на человеке: у кого одно письмо
     * заблокировано, а второе пришло обычным путём, остаётся в сводке по
     * второму.
     */
    contacted: z.object({
        /** `identified + unidentified`. Общий объём обращений за период. */
        total: z.number().int().nonnegative(),
        /**
         * Уникальные `wit_user_id`. ЭТО знаменатель всех метрик про алертов и
         * китов — только про этих людей вообще есть что сказать.
         */
        identified: z.number().int().nonnegative(),
        /**
         * Контакты без `wit_user_id`: биллинг их не опознал. В метрики про алертов
         * и китов не попадают НИКАК — ни в числитель, ни в знаменатель. Считаются
         * по контактам (склеенные контакты сворачиваются в один), потому что
         * другого ключа тождества у неопознанного человека нет: два адреса одного
         * и того же клиента здесь неизбежно дадут двойку.
         */
        unidentified: z.number().int().nonnegative(),
    }),
    /**
     * Метрики 2 и 3: охват алертами и их среднее число.
     *
     * «АЛЕРТ» ЗДЕСЬ — ЭТО КЕЙС (`risk_case`), ТО ЕСТЬ ОДИН ИНЦИДЕНТ, а не строка
     * `risk_alert`. Разница принципиальная: на одну спорную транзакцию приходит
     * до четырёх записей из трёх источников (Ethoca и CDRN внутри Chargeblast,
     * тот же диспут из Stripe, информационный TC40), и среднее по сырым строкам
     * измеряло бы дублирование ИСТОЧНИКОВ, а не поведение клиента — при
     * подключении второго провайдера оно бы удвоилось, хотя клиенты не изменили
     * бы ничего. Оператор в разделе тоже видит один кейс на инцидент.
     */
    alerts: z.object({
        /** Метрика 2, числитель: у скольких обратившихся есть хотя бы один кейс. */
        usersWithAlerts: z.number().int().nonnegative(),
        /** Всего кейсов у обратившихся — общий числитель обоих средних. */
        casesTotal: z.number().int().nonnegative(),
        /**
         * Метрика 3, вариант «среди ВСЕХ»: `casesTotal / contacted.identified`.
         * Отвечает на «сколько алертов приносит средний обратившийся» и включает
         * нули — тех, у кого алертов нет. Всегда МЕНЬШЕ второго варианта.
         */
        avgAlertsPerUser: z.number().nonnegative(),
        /**
         * Метрика 3, вариант «среди ЗАТРОНУТЫХ»: `casesTotal / usersWithAlerts`.
         * Отвечает на «сколько алертов у того, у кого они вообще есть», и по
         * построению >= 1 (либо 0, когда затронутых нет).
         *
         * Оба варианта отданы вместе намеренно: это разные числа, и вопрос
         * «сколько в среднем у пользователей алертов» без уточнения знаменателя
         * ответа не имеет. Одно из них в отрыве от другого читатель неизбежно
         * примет за второе.
         */
        avgAlertsPerUserWithAlerts: z.number().nonnegative(),
    }),
    /**
     * Метрики 4–7 по причинам обращения. Три когорты — это РАЗБИЕНИЕ, а не три
     * произвольных среза: они попарно не пересекаются и вместе покрывают всех,
     * кто за период затронул хотя бы одну из двух тем. Отсюда главное свойство,
     * на которое опирается читатель: `refundOnly.users + cancellationOnly.users +
     * refundAndCancellation.users` — ровно число таких людей, и никто из них не
     * посчитан дважды.
     *
     * Пересечение вынесено ОТДЕЛЬНОЙ когортой намеренно. Раньше просивший и
     * возврат, и отмену молча уезжал в «Возврат»: сумма сходилась, но отличить
     * его от просившего только возврат было нечем — а это разные обращения с
     * разной ценой ошибки.
     *
     * Принадлежность определяется темами классификатора, привязанными к диалогу
     * (`conversation_topic`): `refund` — «Возврат средств», `cancellation` —
     * «Отмена подписки». Признак берётся по человеку ЦЕЛИКОМ за период, а не по
     * отдельному диалогу: просивший отмену в январе и возврат в марте — это «и то
     * и другое», даже когда темы пришли из разных обращений.
     */
    cohorts: z.object({
        /** Тема возврата есть, темы отмены за весь период НЕТ. */
        refundOnly: DashboardCohortSchema,
        /** Тема отмены есть, темы возврата за весь период НЕТ. */
        cancellationOnly: DashboardCohortSchema,
        /** Есть ОБЕ темы — хоть в одном диалоге, хоть в разных. */
        refundAndCancellation: DashboardCohortSchema,
    }),
});
/**
 * Параметры периода. Обе границы необязательны — пустой запрос означает «за
 * всё время», и это поведение по умолчанию.
 *
 * `to` — граница ИСКЛЮЧАЮЩАЯ (полуинтервал `[from, to)`). Так соседние периоды
 * стыкуются без нахлёста и без дыры: обращение ровно в полночь принадлежит
 * ровно одному месяцу. Включающая верхняя граница потребовала бы от клиента
 * вычитать миллисекунду, и рано или поздно кто-нибудь этого не сделал бы.
 */
export const SupportDashboardQuery = z
    .object({
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
})
    /**
     * Пустой по построению период отвергается, а не считается. `from >= to` даёт
     * нули во всех метриках, а нули неотличимы от честного «за этот период никто
     * не обращался» — и разобраться, что запрос был бессмысленным, читателю
     * сводки было бы нечем. Равенство сюда входит намеренно: верхняя граница
     * исключающая, значит `[x, x)` пуст.
     */
    .refine((q) => !(q.from && q.to) || new Date(q.from) < new Date(q.to), {
    message: 'Начало периода должно быть строго раньше конца',
    path: ['to'],
});
//# sourceMappingURL=support-dashboard.js.map